import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./CarrierAccessPage.css";
import { API_BASE_URL } from "../../config";
import { debug } from "../../utils/debug";
import { extractHashLinkFromUrl } from "../../utils/carrierAccessUtils";

interface CarrierAuthResponse {
    success: boolean;
    token: string;
    carrier: {
        partnerId: string;
        tenantId: string;
        lifnr: string;
        name?: string;
        address?: string;
        city?: string;
    };
}

const SESSION_STORAGE_TOKEN_KEY = "carrier_auth_token";
const SESSION_STORAGE_INFO_KEY = "carrier_info";

const CarrierLoginPage: React.FC = () => {
    const navigate = useNavigate();
    const hashLink = extractHashLinkFromUrl();

    const [pin, setPin] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Debug logging
    useEffect(() => {
        debug.log("CarrierLoginPage", "Component mounted", {
            hashLink,
            pathname: window.location.pathname,
            apiBaseUrl: API_BASE_URL
        });
    }, [hashLink]);

    // Check if already authenticated
    useEffect(() => {
        const token = sessionStorage.getItem(SESSION_STORAGE_TOKEN_KEY);
        if (token && hashLink) {
            debug.log("CarrierLoginPage", "Already authenticated, redirecting", { hashLink });
            navigate(`/carrier-access/${hashLink}/transports`);
        }
    }, [hashLink, navigate]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        debug.log("CarrierLoginPage", "Login attempt started", {
            hashLink,
            pinLength: pin.length
        });

        if (!pin.trim()) {
            setError("Bitte PIN eingeben");
            setIsLoading(false);
            return;
        }

        if (!hashLink) {
            debug.error("CarrierLoginPage", "No hashLink available for authentication");
            setError("Ungültiger Link");
            setIsLoading(false);
            return;
        }

        try {
            const apiUrl = `${API_BASE_URL}/api/carrier-access/auth`;
            debug.log("CarrierLoginPage", "Sending auth request", {
                apiUrl,
                hashLink: hashLink.substring(0, 16) + "..."
            });

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    hashLink,
                    pin: pin.trim(),
                }),
            });

            debug.log("CarrierLoginPage", "Response received", {
                status: response.status,
                ok: response.ok
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                debug.warn("CarrierLoginPage", "Authentication failed", errorData);

                if (response.status === 429) {
                    setError(
                        errorData.message ||
                        "Zu viele Fehlversuche. Bitte versuchen Sie es später erneut."
                    );
                } else if (response.status === 401) {
                    if (errorData.error === "Link expired") {
                        setError(
                            "Dieser Zugangslink ist abgelaufen. Bitte kontaktieren Sie Ihren Administrator für einen neuen Link."
                        );
                    } else if (errorData.error === "Link usage limit exceeded") {
                        setError(
                            "Die maximale Anzahl an Verwendungen für diesen Link wurde erreicht. Bitte kontaktieren Sie Ihren Administrator."
                        );
                    } else {
                        setError("Ungültiger Link oder PIN. Bitte überprüfen Sie Ihre Eingaben.");
                    }
                } else {
                    setError(`Fehler: ${errorData.message || "Unbekannter Fehler"}`);
                }
                setIsLoading(false);
                return;
            }

            const data: CarrierAuthResponse = await response.json();
            debug.log("CarrierLoginPage", "Authentication successful", {
                success: data.success,
                hasToken: !!data.token,
                carrier: data.carrier.lifnr
            });

            if (data.success && data.token) {
                debug.log("CarrierLoginPage", "Storing session and redirecting", {
                    redirectUrl: `/carrier-access/${hashLink}/transports`
                });

                // Store token and info in session storage
                sessionStorage.setItem(SESSION_STORAGE_TOKEN_KEY, data.token);
                sessionStorage.setItem(
                    SESSION_STORAGE_INFO_KEY,
                    JSON.stringify({
                        lifnr: data.carrier.lifnr,
                        name: data.carrier.name,
                        address: data.carrier.address,
                        city: data.carrier.city,
                        tenantId: data.carrier.tenantId, // Get tenantId from API response
                        hashLink: hashLink, // Store hashLink for navigation
                    })
                );

                // Redirect to transport list - use stored hashLink
                if (hashLink) {
                    navigate(`/carrier-access/${hashLink}/transports`);
                } else {
                    setError("Ungültiger Link - keine Navigation möglich");
                    setIsLoading(false);
                }
            } else {
                setError("Authentifizierung fehlgeschlagen");
                setIsLoading(false);
            }
        } catch (err) {
            debug.error("CarrierLoginPage", "Request failed", err);
            setError("Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung.");
            setIsLoading(false);
        }
    };

    return (
        <div className="carrier-login-page">
            <div className="carrier-login-container">
                <div className="carrier-login-card">
                    <div className="carrier-login-header">
                        <h1>Spediteur Zugang</h1>
                        <p>Bitte geben Sie Ihre Zugangsdaten ein</p>
                    </div>

                    {!hashLink && (
                        <div className="error-message" role="alert">
                            <span className="error-icon">⚠️</span>
                            Ungültiger Zugangslink. Bitte verwenden Sie den vollständigen Link, der Ihnen per Email zugesendet wurde.
                        </div>
                    )}

                    {error && (
                        <div className="error-message" role="alert">
                            <span className="error-icon">⚠️</span>
                            {error}
                        </div>
                    )}

                    {hashLink && (
                        <form onSubmit={handleLogin} className="carrier-login-form">
                            <div className="form-group">
                                <label htmlFor="pin">PIN</label>
                                <input
                                    id="pin"
                                    type="password"
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value)}
                                    placeholder="PIN eingeben"
                                    disabled={isLoading}
                                    required
                                    autoComplete="off"
                                    maxLength={10}
                                />
                                <small className="form-hint">
                                    Ihr persönlicher PIN wurde Ihnen per Email mitgeteilt
                                </small>
                            </div>

                            <button
                                type="submit"
                                className="carrier-login-button"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner"></span>
                                        Anmelden...
                                    </>
                                ) : (
                                    "Anmelden"
                                )}
                            </button>
                        </form>
                    )}

                    <div className="carrier-login-footer">
                        <p className="help-text">
                            Probleme beim Anmelden?{" "}
                            <a href="mailto:support@example.com">Support kontaktieren</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CarrierLoginPage;
